// @ts-nocheck
/**
 * lookups.ts — WP-008 Semantic Validator
 *
 * Builds O(n) lookup tables from a structurally valid TemplateDocument
 * so that semantic checks can run in O(1) per reference instead of O(n).
 *
 * Pure TypeScript — no framework dependencies.
 */

import type {
    AnyScriptDefinition,
    AssetEntry,
    CategoryDefinition,
    ElementDefinition,
    ExpressionDefinition,
    PropertyDefinition,
    TemplateDocument
} from '../../types/canonical';

export interface SemanticLookups {
  elementById: Map<string, ElementDefinition>;
  categoryById: Map<string, CategoryDefinition>;
  propertyById: Map<string, PropertyDefinition>;
  assetEntryById: Map<string, AssetEntry>;
  scriptById: Map<string, AnyScriptDefinition>;
  expressionById: Map<string, ExpressionDefinition>;
  /** All computed value IDs across all elements */
  computedValueIds: Set<string>;
  /** All action IDs per element: elementId -> Set<actionId> */
  actionIdsByElement: Map<string, Set<string>>;
  /** Animation definition ids on the template document */
  animationIds: Set<string>;
  /** Template class ids (DEC-066 / DEC-081) */
  classById: Map<string, true>;
}

/**
 * Builds all lookup tables from a structurally valid TemplateDocument.
 */
export function buildSemanticLookups(document: TemplateDocument): SemanticLookups {
  const elementById = new Map<string, ElementDefinition>();
  const categoryById = new Map<string, CategoryDefinition>();
  const propertyById = new Map<string, PropertyDefinition>();
  const assetEntryById = new Map<string, AssetEntry>();
  const scriptById = new Map<string, AnyScriptDefinition>();
  const expressionById = new Map<string, ExpressionDefinition>();
  const computedValueIds = new Set<string>();
  const actionIdsByElement = new Map<string, Set<string>>();
  const animationIds = new Set<string>();
  const classById = new Map<string, true>();

  for (const animation of document.animations ?? []) {
    animationIds.add(animation.id);
  }

  // ── Elements ──────────────────────────────────────────────────────────────

  for (const element of document.elements) {
    elementById.set(element.id, element);

    const actionIds = new Set<string>();
    for (const action of element.scripts?.actions ?? []) {
      actionIds.add(action.id);
    }
    actionIdsByElement.set(element.id, actionIds);

    for (const computed of element.scripts?.computedValues ?? []) {
      computedValueIds.add(computed.id);
    }
  }

  // ── Categories ────────────────────────────────────────────────────────────

  for (const bundle of document.categories) {
    for (const cat of bundle.categories) {
      categoryById.set(cat.id, cat);
    }
    for (const prop of bundle.properties) {
      propertyById.set(prop.id, prop);
    }
  }

  // ── Assets ────────────────────────────────────────────────────────────────

  for (const entry of document.assets?.entries ?? []) {
    assetEntryById.set(entry.assetId, entry);
  }

  // ── Scripts and expressions ───────────────────────────────────────────────

  for (const script of document.scripts?.scripts ?? []) {
    scriptById.set(script.id, script);
  }
  for (const expr of document.scripts?.expressions ?? []) {
    expressionById.set(expr.id, expr);
  }

  // Class-local libraries (DEC-066) — available before flatten for semantic checks
  for (const cls of document.classes ?? []) {
    classById.set(cls.id, true);
    for (const script of cls.library?.scripts ?? []) {
      if (!scriptById.has(script.id)) {
        scriptById.set(script.id, script);
      }
    }
    for (const expr of cls.library?.expressions ?? []) {
      if (!expressionById.has(expr.id)) {
        expressionById.set(expr.id, expr);
      }
    }
  }

  return {
    elementById,
    categoryById,
    propertyById,
    assetEntryById,
    scriptById,
    expressionById,
    computedValueIds,
    actionIdsByElement,
    animationIds,
    classById,
  };
}
